import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { highlightCode } from './highlightCode';
import { LINK_REL, markdownUrlTransform, safeLinkUrl } from './markdownPolicy';

// Pull the raw text out of a React children tree. react-markdown nests the
// fenced-code string inside the <code> element's children (sometimes wrapped
// in further elements), so we walk it recursively.
function nodeToText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (typeof node === 'object' && 'props' in node) {
    return nodeToText((node as { props?: { children?: ReactNode } }).props?.children);
  }
  return '';
}

// A fenced code block: a header carrying the language label + a copy button,
// sitting over the scrollable <pre>. The inner <code> element (already styled
// by the `code` renderer below) is passed straight through as children.
function CodeBlock({ children }: { children?: ReactNode }) {
  const codeProps =
    children && typeof children === 'object' && 'props' in children
      ? (children as { props?: { className?: string; children?: ReactNode } })
          .props
      : undefined;
  const lang = codeProps?.className?.match(/language-(\w+)/)?.[1] ?? '';
  const text = nodeToText(codeProps?.children).replace(/\n$/, '');

  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (insecure context / permissions) — fail silently.
    }
  };

  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-black/10 dark:border-white/10">
      <div className="flex items-center justify-between border-b border-white/10 bg-code-header px-3 py-1.5">
        <span className="font-mono text-[11px] uppercase tracking-wide text-code-foreground/60">
          {lang || 'code'}
        </span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-code-foreground/60 transition-colors hover:bg-white/10 hover:text-code-foreground"
          title="Copy code"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 text-code-string" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="overflow-x-auto bg-code px-3.5 py-3 text-[12.5px] font-mono leading-[1.7] text-code-foreground">
        <code className="whitespace-pre">{highlightCode(text, lang)}</code>
      </pre>
    </div>
  );
}

// Soteria's LLM streams markdown — bold, italic, lists, inline code,
// tables, links. We render with react-markdown + GFM and Tailwind-style
// the elements so they fit the conversation pane. Block code gets a
// language-labelled header + copy button (CodeBlock); inline code gets
// a primary tint to read as a "term".
export function SoteriaMarkdown({ content }: { content: string }) {
  return (
    <div className="text-[16px] leading-[1.75] text-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={markdownUrlTransform}
        components={{
          p: (props) => <p className="mb-4 last:mb-0" {...props} />,
          strong: (props) => (
            <strong className="font-semibold text-foreground" {...props} />
          ),
          em: (props) => <em className="italic" {...props} />,
          ul: (props) => (
            <ul className="list-disc pl-5 mb-4 space-y-1.5 marker:text-muted-foreground" {...props} />
          ),
          ol: (props) => (
            <ol className="list-decimal pl-5 mb-4 space-y-1.5 marker:text-muted-foreground" {...props} />
          ),
          li: (props) => <li className="leading-[1.75] pl-1" {...props} />,
          h1: (props) => (
            <h1 className="text-[19px] font-semibold text-foreground mt-5 mb-2 tracking-[-0.01em]" {...props} />
          ),
          h2: (props) => (
            <h2 className="text-[17px] font-semibold text-foreground mt-5 mb-2 tracking-[-0.01em]" {...props} />
          ),
          h3: (props) => (
            <h3 className="text-[15px] font-semibold text-foreground mt-4 mb-1.5" {...props} />
          ),
          code: ({ className, children, ...rest }) => {
            const isBlock = (className ?? '').includes('language-');
            if (isBlock) {
              return (
                <code className={`${className ?? ''} block`} {...rest}>
                  {children}
                </code>
              );
            }
            return (
              <code
                className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-[13.5px] font-mono"
                {...rest}
              >
                {children}
              </code>
            );
          },
          pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
          // Links: allowed protocols only, and always opened safely. The
          // spread comes FIRST so model-supplied props cannot override
          // target/rel. See markdownPolicy.ts.
          a: ({ href, children, node: _node, ...rest }) => {
            const safe = safeLinkUrl(href);
            if (!safe) return <span>{children}</span>;
            return (
              <a
                {...rest}
                className="text-primary underline hover:text-primary/90"
                href={safe}
                target="_blank"
                rel={LINK_REL}
              >
                {children}
              </a>
            );
          },
          // Images are never fetched: a remote <img> loads on paint with no
          // click, which makes it a tracking pixel / exfiltration channel.
          // Show the alt text instead.
          img: ({ alt }) => (alt ? <span>{alt}</span> : null),
          table: (props) => (
            <div className="overflow-x-auto mb-3">
              <table className="border-collapse text-[14px]" {...props} />
            </div>
          ),
          th: (props) => (
            <th
              className="border border-border bg-muted px-3 py-1.5 text-left font-semibold"
              {...props}
            />
          ),
          td: (props) => (
            <td className="border border-border px-3 py-1.5" {...props} />
          ),
          blockquote: (props) => (
            <blockquote
              className="border-l-2 border-primary/30 pl-3 italic text-muted-foreground mb-3"
              {...props}
            />
          ),
          hr: () => <hr className="border-border my-4" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
